import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheck,
  faDownload,
  faEye,
  faFilter,
  faRefresh,
  faSearch,
  faTimes,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import messagesApi from "../../services/messagesApi";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const toDateInputValue = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const todayString = () => toDateInputValue(new Date());

const TAB_CONFIG = [
  { key: "provider_access", label: "Provider Access", type: "sms" },
  { key: "mobile_app", label: "Mobile App", type: "sms" },
  { key: "cs", label: "CS", type: "sms" },
  { key: "pending", label: "Pending", type: "sms" },
  { key: "completed", label: "Completed", type: "sms" },
  { key: "approval_requests", label: "Approval Requests", type: "approval" },
  { key: "customer_feedbacks", label: "Customer Feedbacks", type: "feedback" },
];

const SMS_TABS = TAB_CONFIG.filter((tab) => tab.type === "sms").map((tab) => tab.key);

const DEFAULT_SMS_SORT = {
  provider_access: { sortBy: "received_datetime", sortDirection: "asc" },
  mobile_app: { sortBy: "received_datetime", sortDirection: "asc" },
  cs: { sortBy: "received_datetime", sortDirection: "asc" },
  pending: { sortBy: "pending_datetime", sortDirection: "asc" },
  completed: { sortBy: "called_datetime", sortDirection: "desc" },
};

const SMS_COLUMNS = {
  provider_access: [
    ["tbl_id", "#"],
    ["trans_no", "TRN#"],
    ["member_id", "Member ID"],
    ["member_name", "Member Name"],
    ["provider_name", "Provider"],
    ["mobile_no", "Mobile #"],
    ["coverage", "Coverage"],
    ["status", "Status"],
    ["received_datetime", "Received"],
  ],
  mobile_app: [
    ["tbl_id", "#"],
    ["trans_no", "TRN#"],
    ["member_id", "Member ID"],
    ["member_name", "Member Name"],
    ["provider_name", "Provider"],
    ["mobile_no", "Mobile #"],
    ["coverage", "Coverage"],
    ["status", "Status"],
    ["received_datetime", "Received"],
  ],
  cs: [
    ["tbl_id", "#"],
    ["trans_no", "TRN#"],
    ["member_id", "Member ID"],
    ["member_name", "Member Name"],
    ["provider_name", "Provider"],
    ["mobile_no", "Mobile #"],
    ["coverage", "Coverage"],
    ["status", "Status"],
    ["received_datetime", "Received"],
  ],
  pending: [
    ["tbl_id", "#"],
    ["trans_no", "TRN#"],
    ["member_id", "Member ID"],
    ["member_name", "Member Name"],
    ["provider_name", "Provider"],
    ["mobile_no", "Mobile #"],
    ["generated_by", "Generated From"],
    ["coverage", "Coverage"],
    ["status", "Status"],
    ["received_datetime", "Received"],
    ["pending_datetime", "Pending"],
  ],
  completed: [
    ["tbl_id", "#"],
    ["trans_no", "TRN#"],
    ["member_id", "Member ID"],
    ["member_name", "Member Name"],
    ["provider_name", "Provider"],
    ["mobile_no", "Mobile #"],
    ["generated_by", "Generated From"],
    ["coverage", "Coverage"],
    ["status", "Status"],
    ["received_datetime", "Received"],
    ["pending_datetime", "Pending"],
    ["called_datetime", "Called"],
    ["user_id", "User"],
  ],
};

const APPROVAL_COLUMNS = [
  ["trans_no", "TRN#"],
  ["loa_no", "LOA#"],
  ["member_id", "Member ID"],
  ["member_name", "Member Name"],
  ["provider_name", "Provider"],
  ["coverage_code", "Coverage"],
  ["loa_amt", "Amount"],
  ["status", "Status"],
  ["datetime_requested", "Date Requested"],
  ["decision_datetime", "Action Date"],
  ["requested_by_name", "Requested By"],
  ["decision_by_name", "Action By"],
  ["approver_remarks", "Remarks"],
];

const CUSTOMER_SERVICE_LABELS = {
  "CUSTOMER-SERVICE-1": "Availment Process",
  "CUSTOMER-SERVICE-2": "Mobile Application/Technical",
  "CUSTOMER-SERVICE-3": "Perks",
  "CUSTOMER-SERVICE-4": "Benefit Inquiry",
  "CUSTOMER-SERVICE-5": "Reimbursement",
  "CUSTOMER-SERVICE-6": "Feedback",
};

const APPROVAL_STATUS_LABELS = {
  0: "Pending",
  1: "Approved",
  3: "Returned",
  4: "Disapproved",
};

const makeInitialSmsState = () =>
  SMS_TABS.reduce((acc, tab) => {
    const sort = DEFAULT_SMS_SORT[tab] || DEFAULT_SMS_SORT.provider_access;
    acc[tab] = {
      rows: [],
      total: 0,
      page: 1,
      perPage: 10,
      search: "",
      sortBy: sort.sortBy,
      sortDirection: sort.sortDirection,
      loading: false,
      loaded: false,
      error: "",
    };
    return acc;
  }, {});

const display = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const isCustomerServiceCoverage = (coverage) =>
  String(coverage || "").indexOf("CUSTOMER-SERVICE") >= 0;

const formatCoverage = (coverage) => {
  const value = display(coverage);
  if (!isCustomerServiceCoverage(value)) return value;
  const label = CUSTOMER_SERVICE_LABELS[value];
  return label ? `CUSTOMER-SERVICE (${label})` : value;
};

const formatAmount = (value) => {
  const numeric = Number(value || 0);
  if (Number.isNaN(numeric)) return "0.00";
  return numeric.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getStatusTone = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "called" || normalized === "approved") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (normalized === "pending") return "bg-sky-50 text-sky-700 border-sky-200";
  if (normalized === "returned") return "bg-amber-50 text-amber-700 border-amber-200";
  if (normalized === "disapproved") return "bg-red-50 text-red-700 border-red-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const StatusBadge = ({ status, content }) => {
  const label =
    String(content || "").indexOf("Provider generated for approval") >= 0 &&
    String(status || "").toLowerCase() === "new"
      ? "Provider for Approval"
      : display(status);

  return (
    <span className={`inline-flex rounded border px-2 py-0.5 text-xs font-semibold ${getStatusTone(label)}`}>
      {label}
    </span>
  );
};

const getRowText = (row, key) => {
  if (key === "coverage" || key === "coverage_code") return formatCoverage(row?.[key]);
  if (key === "status") return <StatusBadge status={row?.status} content={row?.content} />;
  if (key === "loa_amt") return formatAmount(row?.[key]);
  return display(row?.[key]);
};

const normalizeKey = (key) => String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const FEEDBACK_TEXTAREA_COLUMN_WIDTHS = {
  corp: "w-[280px] min-w-[280px]",
  memberid: "w-[150px] min-w-[150px]",
  provider: "w-[280px] min-w-[280px]",
  membermobile: "w-[170px] min-w-[170px]",
};

const feedbackTextareaWidth = (column) => FEEDBACK_TEXTAREA_COLUMN_WIDTHS[normalizeKey(column)] || "";

const renderFeedbackCell = (row, column) => {
  const textareaWidth = feedbackTextareaWidth(column);
  const value = display(row?.[column]);

  return (
    <td
      key={column}
      className={textareaWidth ? "px-3 py-2 align-top text-slate-700" : "max-w-[320px] whitespace-nowrap px-4 py-3 text-slate-700"}
    >
      {textareaWidth ? (
        <textarea
          aria-label={column}
          readOnly
          rows={2}
          title={value}
          value={value}
          className={`${textareaWidth} resize-none rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs leading-5 text-slate-700 outline-none`}
        />
      ) : (
        value
      )}
    </td>
  );
};

const findColumnKey = (row, candidates) => {
  const keys = Object.keys(row || {});
  const normalizedCandidates = candidates.map(normalizeKey);
  return keys.find((key) => {
    const normalized = normalizeKey(key);
    return normalizedCandidates.some((candidate) => normalized.includes(candidate));
  });
};

const parseRating = (row) => {
  const key = findColumnKey(row, ["rating", "star", "stars", "score"]);
  const value = Number(row?.[key]);
  if (!Number.isFinite(value)) return null;
  return Math.max(1, Math.min(5, Math.round(value)));
};

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const feedbackDate = (row) => {
  const key = findColumnKey(row, ["datetime", "date", "created", "submitted", "feedbackdate"]);
  return parseDateValue(row?.[key]);
};

const feedbackComment = (row) => {
  const key = findColumnKey(row, ["comment", "comments", "remarks", "feedback"]);
  return key ? String(row?.[key] || "").trim() : "";
};

const feedbackMemberId = (row) => {
  const key = findColumnKey(row, ["memberid", "member"]);
  return key ? String(row?.[key] || "").trim() : "";
};

const SortButton = ({ columnKey, label, sortBy, sortDirection, onSort }) => (
  <button
    type="button"
    className="flex w-full items-center justify-between gap-2 text-left text-xs font-semibold uppercase tracking-normal text-slate-600"
    onClick={() => onSort(columnKey)}
  >
    <span>{label}</span>
    {sortBy === columnKey && <span className="text-slate-400">{sortDirection === "asc" ? "ASC" : "DESC"}</span>}
  </button>
);

const Pagination = ({ page, perPage, total, loading, onPage }) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / perPage));
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm">
      <div className="text-slate-600">
        Page <span className="font-semibold text-slate-900">{page}</span> of{" "}
        <span className="font-semibold text-slate-900">{totalPages}</span> - {total || 0} records
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading || page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        <button
          type="button"
          className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading || page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const Modal = ({ title, onClose, children, footer }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 px-4 py-6">
    <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-lg bg-white shadow-2xl">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <button
          type="button"
          className="rounded p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          onClick={onClose}
          aria-label="Close modal"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <div className="max-h-[68vh] overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-4">{footer}</div>}
    </div>
  </div>
);

const MessagesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem("messages.activeTab") || "provider_access");
  const [smsStates, setSmsStates] = useState(makeInitialSmsState);
  const smsStatesRef = useRef(smsStates);
  const [completedFilters, setCompletedFilters] = useState({
    date_type: "called",
    date_from: todayString(),
    date_to: todayString(),
  });
  const completedFiltersRef = useRef(completedFilters);
  const [messageModal, setMessageModal] = useState(null);
  const [messageRemarks, setMessageRemarks] = useState("");

  const [approvalFilters, setApprovalFilters] = useState({
    request_scope: "mine",
    status: "all",
    date_from: todayString(),
    date_to: todayString(),
    search: "",
    sortBy: "datetime_requested",
    sortDirection: "desc",
    page: 1,
    perPage: 20,
  });
  const approvalFiltersRef = useRef(approvalFilters);
  const [approvalState, setApprovalState] = useState({
    rows: [],
    total: 0,
    statusCounts: { all: 0, pending: 0, returned: 0, disapproved: 0, approved: 0 },
    loading: false,
    loaded: false,
    error: "",
  });
  const [approvalDetail, setApprovalDetail] = useState(null);
  const [approvalCountdown, setApprovalCountdown] = useState(10);

  const [feedbackState, setFeedbackState] = useState({
    columns: [],
    rows: [],
    loading: false,
    loaded: false,
    error: "",
  });
  const [feedbackFilters, setFeedbackFilters] = useState({
    search: "",
    ratings: [],
    commentsOnly: false,
    allTime: false,
    todayOnly: true,
    dateFrom: todayString(),
    dateTo: todayString(),
  });

  const feedbackDateParams = useMemo(() => {
    const params = { limit: 10000 };
    if (feedbackFilters.allTime) return params;

    const today = todayString();
    const dateFrom = feedbackFilters.todayOnly ? today : feedbackFilters.dateFrom;
    const dateTo = feedbackFilters.todayOnly ? today : feedbackFilters.dateTo;

    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    return params;
  }, [feedbackFilters.allTime, feedbackFilters.dateFrom, feedbackFilters.dateTo, feedbackFilters.todayOnly]);

  const handleFeedbackTodayToggle = useCallback((checked) => {
    const today = todayString();
    setFeedbackFilters((prev) => ({
      ...prev,
      allTime: checked ? false : prev.allTime,
      todayOnly: checked,
      dateFrom: checked ? today : prev.dateFrom || today,
      dateTo: checked ? today : prev.dateTo || today,
    }));
  }, []);

  const handleFeedbackAllTimeToggle = useCallback((checked) => {
    const today = todayString();
    setFeedbackFilters((prev) => ({
      ...prev,
      allTime: checked,
      todayOnly: checked ? false : prev.todayOnly,
      dateFrom: checked ? "" : prev.dateFrom || today,
      dateTo: checked ? "" : prev.dateTo || today,
    }));
  }, []);

  const handleFeedbackDateChange = useCallback((key, value) => {
    setFeedbackFilters((prev) => ({
      ...prev,
      allTime: false,
      todayOnly: false,
      [key]: value,
    }));
  }, []);

  useEffect(() => {
    smsStatesRef.current = smsStates;
  }, [smsStates]);

  useEffect(() => {
    completedFiltersRef.current = completedFilters;
  }, [completedFilters]);

  useEffect(() => {
    approvalFiltersRef.current = approvalFilters;
  }, [approvalFilters]);

  useEffect(() => {
    localStorage.setItem("messages.activeTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    const handler = (event) => {
      const nextTab = event.detail;
      if (TAB_CONFIG.some((tab) => tab.key === nextTab)) {
        setActiveTab(nextTab);
        if (nextTab === "approval_requests") {
          const pendingDetail = localStorage.getItem("messages.approvalDetail");
          if (pendingDetail) {
            try {
              const parsed = JSON.parse(pendingDetail);
              setApprovalDetail({
                tbl_id: parsed.tbl_id,
                trans_no: parsed.trans_no || "-",
                status: APPROVAL_STATUS_LABELS[parsed.status] || parsed.status || "-",
                action_by: "-",
                action_date: "-",
                remarks: "-",
              });
            } catch (error) {
              localStorage.removeItem("messages.approvalDetail");
            }
          }
        }
      }
    };
    window.addEventListener("messages:set-active-tab", handler);
    return () => window.removeEventListener("messages:set-active-tab", handler);
  }, []);

  const updateSmsState = useCallback((tab, patch) => {
    setSmsStates((prev) => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        ...patch,
      },
    }));
  }, []);

  const loadSms = useCallback(
    async (tab, overrides = {}) => {
      const current = {
        ...(smsStatesRef.current[tab] || {}),
        ...overrides,
      };
      updateSmsState(tab, { ...overrides, loading: true, error: "" });

      try {
        const params = {
          tab,
          page: current.page || 1,
          per_page: current.perPage || 10,
          search_string: current.search || "",
          sort_by: current.sortBy,
          sort_direction: current.sortDirection,
        };

        if (tab === "completed") {
          Object.assign(params, completedFiltersRef.current);
        }

        const response = await messagesApi.getSmsMessages(params);
        updateSmsState(tab, {
          rows: response.data?.data || [],
          total: Number(response.data?.total || response.data?.recordsFiltered || 0),
          page: response.data?.page || current.page || 1,
          perPage: response.data?.per_page || current.perPage || 10,
          loading: false,
          loaded: true,
        });
      } catch (error) {
        updateSmsState(tab, {
          loading: false,
          loaded: true,
          error: error.response?.data?.message || "Unable to load messages.",
        });
      }
    },
    [updateSmsState]
  );

  const loadApproval = useCallback(async (overrides = {}) => {
    const current = { ...approvalFiltersRef.current, ...overrides };
    setApprovalFilters(current);
    setApprovalState((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const response = await messagesApi.getApprovalRequests({
        request_scope: current.request_scope,
        status: current.status,
        date_from: current.date_from,
        date_to: current.date_to,
        search: current.search,
        sort_by: current.sortBy,
        sort_direction: current.sortDirection,
        page: current.page,
        per_page: current.perPage,
      });
      setApprovalState({
        rows: response.data?.data || [],
        total: Number(response.data?.total || response.data?.recordsFiltered || 0),
        statusCounts: response.data?.statusCounts || {
          all: 0,
          pending: 0,
          returned: 0,
          disapproved: 0,
          approved: 0,
        },
        loading: false,
        loaded: true,
        error: "",
      });
      setApprovalCountdown(10);
    } catch (error) {
      setApprovalState((prev) => ({
        ...prev,
        loading: false,
        loaded: true,
        error: error.response?.data?.message || "Unable to load approval requests.",
      }));
    }
  }, []);

  const loadFeedbacks = useCallback(async (params = feedbackDateParams) => {
    setFeedbackState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const response = await messagesApi.getCustomerFeedbacks(params);
      setFeedbackState({
        columns: response.data?.data?.columns || [],
        rows: response.data?.data?.rows || [],
        loading: false,
        loaded: true,
        error: "",
      });
    } catch (error) {
      setFeedbackState((prev) => ({
        ...prev,
        loading: false,
        loaded: true,
        error: error.response?.data?.message || "Unable to load customer feedbacks.",
      }));
    }
  }, [feedbackDateParams]);

  useEffect(() => {
    if (SMS_TABS.includes(activeTab) && !smsStatesRef.current[activeTab]?.loaded) {
      loadSms(activeTab);
    }
    if (activeTab === "approval_requests" && !approvalState.loaded) {
      loadApproval();
    }
  }, [activeTab, approvalState.loaded, feedbackState.loaded, loadApproval, loadFeedbacks, loadSms]);

  useEffect(() => {
    if (activeTab !== "customer_feedbacks") return;
    loadFeedbacks(feedbackDateParams);
  }, [activeTab, feedbackDateParams, loadFeedbacks]);

  useEffect(() => {
    if (activeTab !== "approval_requests") return undefined;

    const interval = window.setInterval(() => {
      setApprovalCountdown((current) => {
        if (current <= 1) {
          loadApproval();
          return 10;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [activeTab, loadApproval]);

  useEffect(() => {
    if (activeTab !== "approval_requests") return;
    const pendingDetail = localStorage.getItem("messages.approvalDetail");
    if (!pendingDetail) return;

    localStorage.removeItem("messages.approvalDetail");
    try {
      const parsed = JSON.parse(pendingDetail);
      if (parsed?.tbl_id) {
        setApprovalDetail({
          tbl_id: parsed.tbl_id,
          trans_no: parsed.trans_no || "-",
          status: APPROVAL_STATUS_LABELS[parsed.status] || parsed.status || "-",
          action_by: "-",
          action_date: "-",
          remarks: "-",
        });
      }
    } catch (error) {
      localStorage.removeItem("messages.approvalDetail");
    }
  }, [activeTab]);

  const activeSmsState = smsStates[activeTab];

  const handleSmsSort = (tab, columnKey) => {
    const current = smsStatesRef.current[tab];
    const nextDirection = current.sortBy === columnKey && current.sortDirection === "asc" ? "desc" : "asc";
    loadSms(tab, {
      page: 1,
      sortBy: columnKey,
      sortDirection: nextDirection,
    });
  };

  const openMessage = (row) => {
    setMessageModal(row);
    setMessageRemarks(row?.remarks === "-" ? "" : row?.remarks || "");
  };

  const reloadLoadedSmsTabs = () => {
    SMS_TABS.forEach((tab) => {
      if (smsStatesRef.current[tab]?.loaded) {
        loadSms(tab);
      }
    });
  };

  const updateMessageStatus = async (status) => {
    if (!messageModal?.tbl_id) return;
    const confirm = await Swal.fire({
      title: "Update SMS Status",
      text:
        status === "called"
          ? "Are you sure you want to update this message's status to called?"
          : "Are you sure you want to update this message's status to pending?",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      await messagesApi.updateSmsStatus(messageModal.tbl_id, {
        status,
        remarks: messageRemarks,
      });
      setMessageModal(null);
      await Swal.fire("Updated", status === "called" ? "Message successfully set as called." : "Message successfully set as pending.", "success");
      reloadLoadedSmsTabs();
    } catch (error) {
      Swal.fire("Unable to update", error.response?.data?.message || "Message status update failed.", "error");
    }
  };

  const goToTransaction = (row) => {
    const transNo = row?.trans_no;
    if (!transNo || transNo === "-") return;
    navigate(`/generate-loa-transaction?edit_trans_no=${encodeURIComponent(transNo)}`, {
      state: {
        editTransaction: {
          trans_no: transNo,
          coverage_code: row?.coverage || row?.coverage_code || "",
        },
      },
    });
  };

  const goToDischarge = (row) => {
    const transNo = row?.trans_no;
    if (!transNo || transNo === "-") return;
    navigate(`/process-discharge/${encodeURIComponent(transNo)}`);
  };

  const canOpenTransaction = (row) =>
    row?.trans_no && row.trans_no !== "-" && !isCustomerServiceCoverage(row?.coverage || row?.coverage_code);

  const requiresRemarksForCalled = (row) => {
    const coverage = String(row?.coverage || "");
    return coverage === "EMERGENCY" || isCustomerServiceCoverage(coverage);
  };

  const calledDisabled = messageModal && requiresRemarksForCalled(messageModal) && messageRemarks.trim() === "";

  const messageFooter = messageModal && (
    <>
      <button
        type="button"
        className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        onClick={() => setMessageModal(null)}
      >
        Close
      </button>
      {String(messageModal.status).toLowerCase() === "new" && (
        <button
          type="button"
          className="rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
          onClick={() => updateMessageStatus("pending")}
        >
          Set as Pending
        </button>
      )}
      {canOpenTransaction(messageModal) && (
        <button
          type="button"
          className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          onClick={() => goToTransaction(messageModal)}
        >
          Go to TRN Page <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
        </button>
      )}
      {String(messageModal.status).toLowerCase() !== "called" && (
        <button
          type="button"
          className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={calledDisabled}
          onClick={() => updateMessageStatus("called")}
        >
          <FontAwesomeIcon icon={faCheck} className="mr-2" />
          Set as Already Called
        </button>
      )}
    </>
  );

  const handleApprovalSort = (columnKey) => {
    const current = approvalFiltersRef.current;
    const nextDirection = current.sortBy === columnKey && current.sortDirection === "asc" ? "desc" : "asc";
    loadApproval({ ...current, sortBy: columnKey, sortDirection: nextDirection, page: 1 });
  };

  const openApprovalDetail = (row) => {
    setApprovalDetail({
      tbl_id: row.tbl_id,
      trans_no: row.trans_no,
      status: row.status,
      action_by: row.decision_by_name,
      action_date: row.decision_datetime,
      remarks: row.approver_remarks,
    });
  };

  const exportCompleted = () => {
    const current = smsStatesRef.current.completed;
    messagesApi.exportCompletedMessages({
      date_type: completedFiltersRef.current.date_type,
      date_from: completedFiltersRef.current.date_from,
      date_to: completedFiltersRef.current.date_to,
      search: current.search || "",
      sort_by: current.sortBy || "called_datetime",
      sort_direction: current.sortDirection || "desc",
    });
  };

  const exportApproval = () => {
    const current = approvalFiltersRef.current;
    messagesApi.exportApprovalRequests({
      request_scope: current.request_scope,
      status: current.status,
      date_from: current.date_from,
      date_to: current.date_to,
      search: current.search || "",
      sort_by: current.sortBy || "datetime_requested",
      sort_direction: current.sortDirection || "desc",
    });
  };

  const filteredFeedbackRows = useMemo(() => {
    const rows = feedbackState.rows || [];
    const filters = feedbackFilters;
    const today = todayString();
    const from = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`) : null;
    const to = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59`) : null;
    const term = filters.search.trim().toLowerCase();

    return rows.filter((row) => {
      const rating = parseRating(row);
      if (filters.ratings.length > 0 && !filters.ratings.includes(String(rating))) return false;

      if (filters.commentsOnly && feedbackComment(row) === "") return false;

      const rowDate = feedbackDate(row);
      if (!filters.allTime) {
        if (!rowDate) return false;
        const rowDay = toDateInputValue(rowDate);
        if (filters.todayOnly && rowDay !== today) return false;
        if (!filters.todayOnly) {
          if (from && rowDate < from) return false;
          if (to && rowDate > to) return false;
        }
      }

      if (term) {
        return Object.values(row || {}).some((value) => String(value || "").toLowerCase().includes(term));
      }

      return true;
    });
  }, [feedbackFilters, feedbackState.rows]);

  const feedbackStats = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let totalRating = 0;
    let ratingRows = 0;
    const trendMap = {};

    filteredFeedbackRows.forEach((row) => {
      const rating = parseRating(row);
      if (rating) {
        counts[rating] += 1;
        totalRating += rating;
        ratingRows += 1;
      }
      const date = feedbackDate(row);
      if (date) {
        const key = toDateInputValue(date);
        trendMap[key] = trendMap[key] || { total: 0, count: 0 };
        if (rating) {
          trendMap[key].total += rating;
          trendMap[key].count += 1;
        }
      }
    });

    const labels = Object.keys(trendMap).sort();
    return {
      counts,
      average: ratingRows ? (totalRating / ratingRows).toFixed(2) : "0.00",
      total: filteredFeedbackRows.length,
      chart: {
        labels,
        datasets: [
          {
            label: "Average Rating",
            data: labels.map((label) => {
              const point = trendMap[label];
              return point.count ? Number((point.total / point.count).toFixed(2)) : 0;
            }),
            borderColor: "#2563eb",
            backgroundColor: "rgba(37, 99, 235, 0.12)",
            tension: 0.25,
          },
        ],
      },
    };
  }, [filteredFeedbackRows]);

  const exportFeedbackCsv = () => {
    const columns = feedbackState.columns;
    const lines = [
      columns.join(","),
      ...filteredFeedbackRows.map((row) =>
        columns
          .map((column) => {
            const value = String(row?.[column] ?? "").replace(/"/g, '""');
            return `"${value}"`;
          })
          .join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `customer_feedbacks_${todayString()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const renderSmsTab = (tab) => {
    const state = smsStates[tab];
    const columns = SMS_COLUMNS[tab] || SMS_COLUMNS.provider_access;

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="min-w-[240px] flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Search</label>
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={state.search}
                onChange={(event) => updateSmsState(tab, { search: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === "Enter") loadSms(tab, { page: 1 });
                }}
                className="w-full rounded border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="TRN, member, provider, mobile, content"
              />
            </div>
          </div>

          {tab === "completed" && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Date Type</label>
                <select
                  value={completedFilters.date_type}
                  onChange={(event) => setCompletedFilters((prev) => ({ ...prev, date_type: event.target.value }))}
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="called">Called</option>
                  <option value="received">Received</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">From</label>
                <input
                  type="date"
                  value={completedFilters.date_from}
                  onChange={(event) => setCompletedFilters((prev) => ({ ...prev, date_from: event.target.value }))}
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">To</label>
                <input
                  type="date"
                  value={completedFilters.date_to}
                  onChange={(event) => setCompletedFilters((prev) => ({ ...prev, date_to: event.target.value }))}
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          <button
            type="button"
            className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            onClick={() => loadSms(tab, { page: 1 })}
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Apply
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => loadSms(tab)}
          >
            <FontAwesomeIcon icon={faRefresh} className="mr-2" />
            Refresh
          </button>
          {tab === "completed" && (
            <button
              type="button"
              className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              onClick={exportCompleted}
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Export CSV
            </button>
          )}
        </div>

        {state.error && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{state.error}</div>}

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="w-40 px-4 py-3 text-left text-xs font-semibold uppercase tracking-normal text-slate-600">Actions</th>
                  {columns.map(([key, label]) => (
                    <th key={key} className="px-4 py-3 text-left">
                      <SortButton
                        columnKey={key}
                        label={label}
                        sortBy={state.sortBy}
                        sortDirection={state.sortDirection}
                        onSort={(columnKey) => handleSmsSort(tab, columnKey)}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {state.loading && (
                  <tr>
                    <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-500">
                      Loading messages...
                    </td>
                  </tr>
                )}
                {!state.loading && state.rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-500">
                      No records found.
                    </td>
                  </tr>
                )}
                {!state.loading &&
                  state.rows.map((row) => {
                    const discharge = String(row.content || "").indexOf("REPORT FOR DISCHARGE") >= 0;
                    return (
                      <tr key={`${tab}-${row.tbl_id}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                              onClick={() => openMessage(row)}
                            >
                              <FontAwesomeIcon icon={faEye} className="mr-1" />
                              View
                            </button>
                            {canOpenTransaction(row) && (
                              discharge ? (
                                <button
                                  type="button"
                                  className="rounded bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700"
                                  onClick={() => goToDischarge(row)}
                                >
                                  Discharge Processing
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                                  onClick={() => goToTransaction(row)}
                                >
                                  TRN Page
                                </button>
                              )
                            )}
                          </div>
                        </td>
                        {columns.map(([key]) => (
                          <td key={key} className="max-w-[260px] whitespace-nowrap px-4 py-3 text-slate-700">
                            {getRowText(row, key)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={state.page}
            perPage={state.perPage}
            total={state.total}
            loading={state.loading}
            onPage={(page) => loadSms(tab, { page })}
          />
        </div>
      </div>
    );
  };

  const renderApprovalTab = () => (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-4 flex flex-wrap gap-2">
          {[
            ["mine", "My Requests", faUser],
            ["others", "Others' Requests", faUsers],
            ["all", "All Requests", null],
          ].map(([value, label, icon]) => (
            <button
              key={value}
              type="button"
              className={`rounded border px-3 py-2 text-sm font-semibold ${
                approvalFilters.request_scope === value
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
              onClick={() => loadApproval({ ...approvalFiltersRef.current, request_scope: value, page: 1 })}
            >
              {icon && <FontAwesomeIcon icon={icon} className="mr-2" />}
              {label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,1fr)_160px_160px_auto]">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Search</label>
            <input
              type="text"
              value={approvalFilters.search}
              onChange={(event) => setApprovalFilters((prev) => ({ ...prev, search: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === "Enter") loadApproval({ ...approvalFiltersRef.current, page: 1 });
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="TRN, LOA, member, provider, remarks"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">From</label>
            <input
              type="date"
              value={approvalFilters.date_from}
              onChange={(event) => setApprovalFilters((prev) => ({ ...prev, date_from: event.target.value }))}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">To</label>
            <input
              type="date"
              value={approvalFilters.date_to}
              onChange={(event) => setApprovalFilters((prev) => ({ ...prev, date_to: event.target.value }))}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <button
              type="button"
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              onClick={() => loadApproval({ ...approvalFiltersRef.current, page: 1 })}
            >
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Apply
            </button>
            <button
              type="button"
              className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              onClick={exportApproval}
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Export Excel
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {[
            ["all", "All"],
            ["pending", "Pending"],
            ["returned", "Returned"],
            ["disapproved", "Disapproved"],
            ["approved", "Approved"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`rounded border px-3 py-1.5 text-sm font-semibold ${
                approvalFilters.status === value
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
              onClick={() => loadApproval({ ...approvalFiltersRef.current, status: value, page: 1 })}
            >
              {label} <span className="ml-1 rounded bg-white/20 px-1">{approvalState.statusCounts?.[value] || 0}</span>
            </button>
          ))}
          <span className="ml-auto text-sm text-slate-500">Auto refresh in {approvalCountdown}s</span>
        </div>
      </div>

      {approvalState.error && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{approvalState.error}</div>}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-44 px-4 py-3 text-left text-xs font-semibold uppercase tracking-normal text-slate-600">Action</th>
                {APPROVAL_COLUMNS.map(([key, label]) => (
                  <th key={key} className="px-4 py-3 text-left">
                    <SortButton
                      columnKey={key}
                      label={label}
                      sortBy={approvalFilters.sortBy}
                      sortDirection={approvalFilters.sortDirection}
                      onSort={handleApprovalSort}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {approvalState.loading && (
                <tr>
                  <td colSpan={APPROVAL_COLUMNS.length + 1} className="px-4 py-8 text-center text-slate-500">
                    Loading approval requests...
                  </td>
                </tr>
              )}
              {!approvalState.loading && approvalState.rows.length === 0 && (
                <tr>
                  <td colSpan={APPROVAL_COLUMNS.length + 1} className="px-4 py-8 text-center text-slate-500">
                    No approval requests found.
                  </td>
                </tr>
              )}
              {!approvalState.loading &&
                approvalState.rows.map((row) => (
                  <tr key={`approval-${row.tbl_id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          onClick={() => openApprovalDetail(row)}
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-1" />
                          View
                        </button>
                        <button
                          type="button"
                          className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                          onClick={() => goToTransaction(row)}
                        >
                          TRN Page
                        </button>
                      </div>
                    </td>
                    {APPROVAL_COLUMNS.map(([key]) => (
                      <td key={key} className="max-w-[260px] whitespace-nowrap px-4 py-3 text-slate-700">
                        {getRowText(row, key)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={approvalFilters.page}
          perPage={approvalFilters.perPage}
          total={approvalState.total}
          loading={approvalState.loading}
          onPage={(page) => loadApproval({ ...approvalFiltersRef.current, page })}
        />
      </div>
    </div>
  );

  const renderFeedbackTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Average</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{feedbackStats.average}</div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Total</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{feedbackStats.total}</div>
            </div>
            {[5, 4].map((rating) => (
              <div key={rating}>
                <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">{rating} Stars</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">{feedbackStats.counts[rating] || 0}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 h-64">
            <Line
              data={feedbackStats.chart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { min: 0, max: 5 } },
              }}
            />
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Search</label>
              <input
                type="text"
                value={feedbackFilters.search}
                onChange={(event) => setFeedbackFilters((prev) => ({ ...prev, search: event.target.value }))}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="Search feedbacks"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Ratings</label>
              <div className="flex flex-wrap gap-2">
                {["1", "2", "3", "4", "5"].map((rating) => (
                  <label key={rating} className="flex items-center gap-2 rounded border border-slate-300 px-3 py-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={feedbackFilters.ratings.includes(rating)}
                      onChange={(event) =>
                        setFeedbackFilters((prev) => ({
                          ...prev,
                          ratings: event.target.checked
                            ? [...prev.ratings, rating]
                            : prev.ratings.filter((item) => item !== rating),
                        }))
                      }
                    />
                    {rating}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={feedbackFilters.commentsOnly}
                onChange={(event) => setFeedbackFilters((prev) => ({ ...prev, commentsOnly: event.target.checked }))}
              />
              Comments only
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={feedbackFilters.todayOnly}
                  disabled={feedbackFilters.allTime}
                  onChange={(event) => handleFeedbackTodayToggle(event.target.checked)}
                />
                Today
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={feedbackFilters.allTime}
                  onChange={(event) => handleFeedbackAllTimeToggle(event.target.checked)}
                />
                All Time
              </label>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold uppercase tracking-normal text-slate-500">
                From Date
                <input
                  type="date"
                  value={feedbackFilters.dateFrom}
                  disabled={feedbackFilters.todayOnly || feedbackFilters.allTime}
                  onChange={(event) => handleFeedbackDateChange("dateFrom", event.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-normal text-slate-500">
                To Date
                <input
                  type="date"
                  value={feedbackFilters.dateTo}
                  disabled={feedbackFilters.todayOnly || feedbackFilters.allTime}
                  onChange={(event) => handleFeedbackDateChange("dateTo", event.target.value)}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => loadFeedbacks()}
              >
                <FontAwesomeIcon icon={faRefresh} className="mr-2" />
                Refresh
              </button>
              <button
                type="button"
                className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                onClick={exportFeedbackCsv}
              >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {feedbackState.error && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{feedbackState.error}</div>}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-normal text-slate-600">Action</th>
                {feedbackState.columns.map((column) => (
                  <th key={column} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-normal text-slate-600">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {feedbackState.loading && (
                <tr>
                  <td colSpan={feedbackState.columns.length + 1} className="px-4 py-8 text-center text-slate-500">
                    Loading customer feedbacks...
                  </td>
                </tr>
              )}
              {!feedbackState.loading && filteredFeedbackRows.length === 0 && (
                <tr>
                  <td colSpan={feedbackState.columns.length + 1} className="px-4 py-8 text-center text-slate-500">
                    No customer feedbacks found.
                  </td>
                </tr>
              )}
              {!feedbackState.loading &&
                filteredFeedbackRows.map((row, index) => {
                  const memberId = feedbackMemberId(row);
                  return (
                    <tr key={`feedback-${index}`} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          type="button"
                          className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={!memberId}
                          onClick={() => navigate(`/members/view/full-page?member_id=${encodeURIComponent(memberId)}`)}
                        >
                          View Member Info
                        </button>
                      </td>
                      {feedbackState.columns.map((column) => renderFeedbackCell(row, column))}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Messages</h1>
          <p className="text-sm text-slate-500">Provider access, mobile app, CS, pending, completed, approvals, and customer feedbacks.</p>
        </div>
        <button
          type="button"
          className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          onClick={() => {
            if (SMS_TABS.includes(activeTab)) loadSms(activeTab);
            if (activeTab === "approval_requests") loadApproval();
            if (activeTab === "customer_feedbacks") loadFeedbacks();
          }}
        >
          <FontAwesomeIcon icon={faRefresh} className="mr-2" />
          Refresh
        </button>
      </div>

      <div className="mb-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <div className="flex min-w-max">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`border-r border-slate-200 px-4 py-3 text-sm font-semibold ${
                activeTab === tab.key ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-50"
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {SMS_TABS.includes(activeTab) && activeSmsState && renderSmsTab(activeTab)}
      {activeTab === "approval_requests" && renderApprovalTab()}
      {activeTab === "customer_feedbacks" && renderFeedbackTab()}

      {messageModal && (
        <Modal title="Message Details" onClose={() => setMessageModal(null)} footer={messageFooter}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              ["ID", messageModal.member_id],
              ["Name", messageModal.member_name],
              ["Mobile #", messageModal.mobile_no],
              ["Coverage", formatCoverage(messageModal.coverage)],
              ["Provider", messageModal.provider_name],
              ["Status", messageModal.status],
              ["Date & Time Received", messageModal.received_datetime],
              ["Date & Time Pending", messageModal.pending_datetime],
              ["Date & Time Called", messageModal.called_datetime],
              ["User Called", messageModal.user_id],
            ].map(([label, value]) => (
              <div key={label}>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">{label}</label>
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{display(value)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Message Body</label>
              <textarea
                readOnly
                value={messageModal.content === "-" ? "" : messageModal.content || ""}
                className="h-40 w-full rounded border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Remarks</label>
              <textarea
                readOnly={String(messageModal.status).toLowerCase() === "called"}
                value={messageRemarks}
                onChange={(event) => setMessageRemarks(event.target.value)}
                className="h-40 w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 disabled:bg-slate-50"
              />
              {calledDisabled && <p className="mt-2 text-xs text-red-600">Remarks are required before setting this message as called.</p>}
            </div>
          </div>
        </Modal>
      )}

      {approvalDetail && (
        <Modal
          title="Approval Request Details"
          onClose={() => setApprovalDetail(null)}
          footer={
            <button
              type="button"
              className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => setApprovalDetail(null)}
            >
              Close
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[
              ["Transaction Number", approvalDetail.trans_no],
              ["Status", approvalDetail.status],
              ["Action By", approvalDetail.action_by],
              ["Action Date", approvalDetail.action_date],
            ].map(([label, value]) => (
              <div key={label}>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">{label}</label>
                <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">{display(value)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-normal text-slate-500">Remarks</label>
            <textarea
              readOnly
              value={approvalDetail.remarks || "-"}
              className="h-36 w-full rounded border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MessagesPage;
